import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  FormSelect,
  FormTextarea,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { centsToUsd } from "@/lib/money";
import { useBookingSlots, useResources } from "@/api/hooks/useBookings";
import { useCreateParty, usePartyActivities } from "@/api/hooks/useParties";
import {
  PARTY_FORMAT_LABELS,
  PARTY_OCCASION_LABELS,
  PARTY_SELECTION_COUNT_BY_FORMAT,
  type PartyActivityCatalogItem,
  type PartyFormat,
  type PartyOccasion,
} from "@/api/types/party";
import type { BookingStatus } from "@/api/types/booking";
import { isWeekendIso, totalPriceCentsPreviewWithSelections } from "./partyMath";

interface CreatePartyDialogProps {
  open: boolean;
  onClose: () => void;
  defaultDate?: string;
  onCreated?: () => void;
}

// Zone A: parties book exclusively against The Nest — this dialog no
// longer offers a room picker (dropped entirely rather than kept as a
// disabled single-option select; the room is a fixed fact of the product
// now, not a form field with exactly one legal answer). The Nest's own
// resource_id is derived from the existing resources list by name.
const PARTY_RESOURCE_NAME = "The Nest";

/**
 * Staff party-create form: parties are always booked against The Nest
 * (never any other event room) on a weekend date only — a non-weekend
 * date is caught client-side here AND rejected (422) server-side either
 * way. Zone A: a format toggle (Stations / Project-based) drives a
 * catalog picker enforcing exactly 3 / exactly 10 selections before
 * submit is enabled. Live price preview via partyMath.ts (PREVIEW ONLY —
 * the party's real price, returned by the create call, is always the
 * number of record).
 */
export function CreatePartyDialog({ open, onClose, defaultDate, onCreated }: CreatePartyDialogProps) {
  const { data: resources } = useResources();
  const nest = useMemo(() => (resources ?? []).find((r) => r.name === PARTY_RESOURCE_NAME), [resources]);

  const [bookingDate, setBookingDate] = useState(defaultDate ?? "");
  const [slotId, setSlotId] = useState("");
  const [extraHour, setExtraHour] = useState(false);
  const [status, setStatus] = useState<BookingStatus>("confirmed");
  const [headcount, setHeadcount] = useState("10");
  const [occasion, setOccasion] = useState<PartyOccasion>("birthday");
  const [childAge, setChildAge] = useState("");
  const [notes, setNotes] = useState("");
  const [format, setFormat] = useState<PartyFormat>("stations");
  const [selections, setSelections] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setBookingDate(defaultDate ?? "");
      setSlotId("");
      setExtraHour(false);
      setStatus("confirmed");
      setHeadcount("10");
      setOccasion("birthday");
      setChildAge("");
      setNotes("");
      setFormat("stations");
      setSelections([]);
    }
  }, [open, defaultDate]);

  // Reset selections whenever the format changes — a stations selection
  // isn't valid once switched to project_based and vice versa.
  useEffect(() => {
    setSelections([]);
  }, [format]);

  const { data: slots } = useBookingSlots("event_room");
  const { data: activities } = usePartyActivities();
  const createParty = useCreateParty();

  const catalogForFormat = useMemo(
    () => (activities ?? []).filter((a) => a.format === format),
    [activities, format],
  );
  const selectedItems: PartyActivityCatalogItem[] = useMemo(
    () => catalogForFormat.filter((a) => selections.includes(a.id)),
    [catalogForFormat, selections],
  );

  const requiredCount = PARTY_SELECTION_COUNT_BY_FORMAT[format];
  const headcountNumber = headcount.trim() ? Number(headcount) : null;

  function toggleSelection(id: string) {
    setSelections((prev) => {
      if (prev.includes(id)) return prev.filter((s) => s !== id);
      if (prev.length >= requiredCount) return prev; // already at the exact count
      return [...prev, id];
    });
  }

  const isWeekend = bookingDate ? isWeekendIso(bookingDate) : true;
  const priceCents = bookingDate
    ? totalPriceCentsPreviewWithSelections(extraHour, [], selectedItems, headcountNumber)
    : null;

  const canSubmit =
    !!nest &&
    !!bookingDate &&
    !!slotId &&
    isWeekend &&
    selections.length === requiredCount &&
    !createParty.isPending;

  async function handleSubmit() {
    if (!canSubmit || !nest) return;
    try {
      await createParty.mutateAsync({
        resource_id: nest.id,
        slot_id: slotId,
        booking_date: bookingDate,
        extra_hour: extraHour,
        status,
        headcount: headcountNumber,
        occasion,
        child_age: childAge.trim() ? Number(childAge) : null,
        notes: notes.trim() || null,
        format,
        selections,
      });
      toastSuccess("Party booked");
      onCreated?.();
      onClose();
    } catch (err) {
      toastError("Couldn't book party", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="New party">
      <DialogContent size="md">
        <DialogHeader onClose={onClose}>New party</DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-text-secondary">
            Every party is held at <span className="font-semibold text-text-primary">The Nest</span> — a
            dedicated building with its own garden/play area.
          </p>

          <FormField
            label="Date (weekends only)"
            type="date"
            value={bookingDate}
            onChange={(e) => setBookingDate(e.target.value)}
            data-testid="party-date-input"
          />
          {bookingDate && !isWeekend && (
            <p className="text-sm text-danger" role="alert">
              Parties are a weekend-only product — pick a Saturday or Sunday.
            </p>
          )}

          <FormSelect label="Time slot" value={slotId} onChange={(e) => setSlotId(e.target.value)}>
            <option value="">Select a slot…</option>
            {(slots ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </FormSelect>

          <Checkbox
            label="Add an adjacent hour (+$75 flat)"
            checked={extraHour}
            onChange={(e) => setExtraHour(e.target.checked)}
          />

          <FormSelect label="Status" value={status} onChange={(e) => setStatus(e.target.value as BookingStatus)}>
            <option value="confirmed">Confirmed</option>
            <option value="inquiry">Inquiry</option>
          </FormSelect>

          <FormSelect label="Occasion" value={occasion} onChange={(e) => setOccasion(e.target.value as PartyOccasion)}>
            {Object.entries(PARTY_OCCASION_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </FormSelect>

          <FormField
            label="Headcount"
            type="number"
            min={0}
            value={headcount}
            onChange={(e) => setHeadcount(e.target.value)}
          />

          <FormField
            label="Birthday child's age (optional)"
            type="number"
            min={0}
            max={18}
            value={childAge}
            onChange={(e) => setChildAge(e.target.value)}
          />

          <FormTextarea label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />

          <div className="space-y-2 rounded-lg border border-border p-3">
            <FormSelect label="Format" value={format} onChange={(e) => setFormat(e.target.value as PartyFormat)}>
              {Object.entries(PARTY_FORMAT_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </FormSelect>

            <p className="text-sm text-text-secondary" data-testid="party-selection-counter">
              {selections.length} of {requiredCount} selected
              {format === "stations" ? " (included, free)" : " (see prices below)"}
            </p>

            <div className="grid max-h-56 grid-cols-1 gap-1 overflow-y-auto sm:grid-cols-2" data-testid="party-catalog-picker">
              {catalogForFormat.map((item) => {
                const checked = selections.includes(item.id);
                const disabled = !checked && selections.length >= requiredCount;
                const previewCents = item.base_price_cents + item.premium_per_kid_surcharge_cents * (headcountNumber ?? 0);
                return (
                  <Checkbox
                    key={item.id}
                    label={format === "stations" ? item.name : `${item.name} (${centsToUsd(previewCents)})`}
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggleSelection(item.id)}
                    data-testid={`party-catalog-item-${item.id}`}
                  />
                );
              })}
            </div>
          </div>

          {priceCents !== null && (
            <p className="text-sm text-text-secondary" data-testid="party-price-preview">
              Price preview: <span className="font-semibold text-text-primary">{centsToUsd(priceCents)}</span>
              {" "}(add-ons can be added at booking or afterward via the party portal)
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={createParty.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit} data-testid="submit-party-form">
            {createParty.isPending ? "Booking…" : "Book party"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
